/**
 * /payroll-l3/memberships — Employee union membership management.
 *
 * Endpoints:
 *   GET   /api/payroll/employees
 *   GET   /api/payroll-l3/employees/{employee_id}/memberships
 *   POST  /api/payroll-l3/employees/{employee_id}/memberships
 *   PATCH /api/payroll-l3/memberships/{membership_id}
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l3/memberships")({
  component: MembershipsPage,
});

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  employee_number: string | null;
}

interface Local {
  id: string;
  name: string;
}

interface Membership {
  id: string;
  employee_id: string;
  local_id: string;
  member_number: string | null;
  effective_start: string;
  effective_end: string | null;
  dues_checkoff_auth: boolean;
  dues_checkoff_auth_date: string | null;
  current_period: number | null;
  hours_this_period: string;
  created_at: string;
}

function fmtDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// ─── Add membership dialog ────────────────────────────────────────────────────

function AddMembershipDialog({
  open,
  onClose,
  employeeId,
  locals,
}: {
  open: boolean;
  onClose: () => void;
  employeeId: string;
  locals: Local[];
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    local_id: "",
    member_number: "",
    effective_start: "",
    effective_end: "",
    dues_checkoff_auth: false,
    dues_checkoff_auth_date: "",
    current_period: "",
  });
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api<Membership>(
        `/api/payroll-l3/employees/${employeeId}/memberships`,
        {
          method: "POST",
          body: {
            employee_id: employeeId,
            local_id: form.local_id,
            member_number: form.member_number || null,
            effective_start: form.effective_start,
            effective_end: form.effective_end || null,
            dues_checkoff_auth: form.dues_checkoff_auth,
            dues_checkoff_auth_date: form.dues_checkoff_auth_date || null,
            current_period: form.current_period ? parseInt(form.current_period, 10) : null,
          },
        }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l3", "memberships", employeeId] });
      onClose();
      setForm({ local_id: "", member_number: "", effective_start: "", effective_end: "", dues_checkoff_auth: false, dues_checkoff_auth_date: "", current_period: "" });
      setErr(null);
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Failed to create membership"),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <span>Add Union Membership</span>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={0.5}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField
            select label="Local *" size="small" fullWidth
            value={form.local_id}
            onChange={(e) => setForm((p) => ({ ...p, local_id: e.target.value }))}
          >
            {locals.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
          </TextField>
          <TextField
            label="Member / Card Number" size="small" fullWidth
            value={form.member_number}
            onChange={(e) => setForm((p) => ({ ...p, member_number: e.target.value }))}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              type="date" label="Effective Start *" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.effective_start}
              onChange={(e) => setForm((p) => ({ ...p, effective_start: e.target.value }))}
            />
            <TextField
              type="date" label="Effective End" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.effective_end}
              onChange={(e) => setForm((p) => ({ ...p, effective_end: e.target.value }))}
            />
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={form.dues_checkoff_auth}
                onChange={(e) => setForm((p) => ({ ...p, dues_checkoff_auth: e.target.checked }))}
              />
            }
            label="Dues check-off authorized"
          />
          {form.dues_checkoff_auth && (
            <TextField
              type="date" label="Authorization Date" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.dues_checkoff_auth_date}
              onChange={(e) => setForm((p) => ({ ...p, dues_checkoff_auth_date: e.target.value }))}
            />
          )}
          <TextField
            label="Current Apprentice Period" size="small" fullWidth type="number"
            inputProps={{ min: 0 }}
            value={form.current_period}
            onChange={(e) => setForm((p) => ({ ...p, current_period: e.target.value }))}
            helperText="Leave blank for journeyman workers"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!form.local_id || !form.effective_start || mut.isPending}
          onClick={() => { setErr(null); mut.mutate(); }}
        >
          Add Membership
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Edit membership dialog ───────────────────────────────────────────────────

function EditMembershipDialog({
  membership,
  onClose,
  employeeId,
  localName,
}: {
  membership: Membership | null;
  onClose: () => void;
  employeeId: string;
  localName: (id: string) => string;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<{
    effective_end: string;
    dues_checkoff_auth: boolean;
    dues_checkoff_auth_date: string;
    current_period: string;
    member_number: string;
  }>({
    effective_end: membership?.effective_end ?? "",
    dues_checkoff_auth: membership?.dues_checkoff_auth ?? false,
    dues_checkoff_auth_date: membership?.dues_checkoff_auth_date ?? "",
    current_period: membership?.current_period?.toString() ?? "",
    member_number: membership?.member_number ?? "",
  });
  const [err, setErr] = useState<string | null>(null);

  // Sync form when membership changes
  if (membership && form.effective_end === "" && membership.effective_end) {
    setForm((p) => ({ ...p, effective_end: membership.effective_end ?? "" }));
  }

  const mut = useMutation({
    mutationFn: () =>
      api<Membership>(`/api/payroll-l3/memberships/${membership!.id}`, {
        method: "PATCH",
        body: {
          effective_end: form.effective_end || null,
          dues_checkoff_auth: form.dues_checkoff_auth,
          dues_checkoff_auth_date: form.dues_checkoff_auth_date || null,
          current_period: form.current_period ? parseInt(form.current_period, 10) : null,
          member_number: form.member_number || null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l3", "memberships", employeeId] });
      onClose();
      setErr(null);
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Failed to update"),
  });

  if (!membership) return null;

  return (
    <Dialog open={!!membership} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <span>Edit Membership — {localName(membership.local_id)}</span>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={0.5}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField
            label="Member / Card Number" size="small" fullWidth
            value={form.member_number}
            onChange={(e) => setForm((p) => ({ ...p, member_number: e.target.value }))}
          />
          <TextField
            type="date" label="Effective End" size="small" fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.effective_end}
            onChange={(e) => setForm((p) => ({ ...p, effective_end: e.target.value }))}
            helperText="Set to terminate this membership"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.dues_checkoff_auth}
                onChange={(e) => setForm((p) => ({ ...p, dues_checkoff_auth: e.target.checked }))}
              />
            }
            label="Dues check-off authorized"
          />
          {form.dues_checkoff_auth && (
            <TextField
              type="date" label="Authorization Date" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.dues_checkoff_auth_date}
              onChange={(e) => setForm((p) => ({ ...p, dues_checkoff_auth_date: e.target.value }))}
            />
          )}
          <TextField
            label="Current Apprentice Period" size="small" fullWidth type="number"
            inputProps={{ min: 0 }}
            value={form.current_period}
            onChange={(e) => setForm((p) => ({ ...p, current_period: e.target.value }))}
            helperText="Leave blank for journeyman workers"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mut.isPending} onClick={() => { setErr(null); mut.mutate(); }}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function MembershipsPage() {
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Membership | null>(null);

  const employeesQ = useQuery({
    queryKey: ["payroll", "employees"],
    queryFn: () => api<Employee[]>("/api/payroll/employees?active_only=false"),
  });

  const localsQ = useQuery({
    queryKey: ["payroll-l3", "locals"],
    queryFn: () => api<Local[]>("/api/payroll-l3/locals"),
  });

  const membershipsQ = useQuery({
    queryKey: ["payroll-l3", "memberships", selectedEmployee?.id],
    queryFn: () =>
      api<Membership[]>(
        `/api/payroll-l3/employees/${selectedEmployee!.id}/memberships`
      ),
    enabled: !!selectedEmployee,
  });

  const localName = (id: string) =>
    localsQ.data?.find((l) => l.id === id)?.name ?? id.slice(0, 8) + "…";

  const empLabel = (e: Employee) =>
    `${e.first_name} ${e.last_name}${e.employee_number ? ` (#${e.employee_number})` : ""}`;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>Employee Union Memberships</Typography>
        <HelpHint docPath="payroll-l3/memberships.md" label="Memberships help" />
      </Stack>

      {/* Employee selector */}
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <TextField
          select
          size="small"
          label="Select Employee"
          value={selectedEmployee?.id ?? ""}
          onChange={(e) => {
            const emp = employeesQ.data?.find((x) => x.id === e.target.value) ?? null;
            setSelectedEmployee(emp);
          }}
          sx={{ minWidth: 320 }}
        >
          {employeesQ.isLoading && <MenuItem disabled>Loading…</MenuItem>}
          {(employeesQ.data ?? []).map((emp) => (
            <MenuItem key={emp.id} value={emp.id}>
              {empLabel(emp)}
            </MenuItem>
          ))}
        </TextField>
        {selectedEmployee && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
          >
            Add Membership
          </Button>
        )}
      </Stack>

      {/* Membership table */}
      {selectedEmployee && (
        <>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            {empLabel(selectedEmployee)} — Union Memberships
          </Typography>

          {membershipsQ.isLoading && <CircularProgress size={24} />}
          {membershipsQ.error && (
            <Alert severity="error">Failed to load memberships.</Alert>
          )}

          {membershipsQ.data && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Local</TableCell>
                  <TableCell>Card #</TableCell>
                  <TableCell>Effective Start</TableCell>
                  <TableCell>Effective End</TableCell>
                  <TableCell>Dues Auth</TableCell>
                  <TableCell>Apprentice Period</TableCell>
                  <TableCell>Hours This Period</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {membershipsQ.data.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{localName(m.local_id)}</TableCell>
                    <TableCell>{m.member_number ?? "—"}</TableCell>
                    <TableCell>{fmtDate(m.effective_start)}</TableCell>
                    <TableCell>
                      {m.effective_end ? (
                        <Chip label={fmtDate(m.effective_end)} size="small" color="default" />
                      ) : (
                        <Chip label="Active" size="small" color="success" />
                      )}
                    </TableCell>
                    <TableCell>
                      {m.dues_checkoff_auth ? (
                        <Chip label="Auth'd" size="small" color="success" />
                      ) : (
                        <Chip label="No" size="small" />
                      )}
                    </TableCell>
                    <TableCell>{m.current_period ?? "—"}</TableCell>
                    <TableCell>{Number(m.hours_this_period).toFixed(1)}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => setEditTarget(m)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {membershipsQ.data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ color: "text.secondary", py: 4 }}>
                      No union memberships on file for this employee.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </>
      )}

      {!selectedEmployee && !employeesQ.isLoading && (
        <Box sx={{ color: "text.secondary", textAlign: "center", py: 6 }}>
          <Typography>Select an employee above to view or edit their union memberships.</Typography>
        </Box>
      )}

      <AddMembershipDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        employeeId={selectedEmployee?.id ?? ""}
        locals={localsQ.data ?? []}
      />

      <EditMembershipDialog
        membership={editTarget}
        onClose={() => setEditTarget(null)}
        employeeId={selectedEmployee?.id ?? ""}
        localName={localName}
      />
    </Box>
  );
}
